import React, { useState, useEffect, useMemo, useRef } from "react";
import Dropdown from "components/dropdown";
import { FiAlignJustify, FiSearch } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import { MdLogout, MdOutlinePalette, MdKeyboardArrowDown, MdCheck } from "react-icons/md";

// --- İMPORTLAR ---
import { useAuth } from "context/AuthContext";
import { useThemeCustomizer } from "context/ThemeContext"; // TEMA MOTORU
import routes from "routes.js";

const Navbar = (props) => {
  const { onOpenSidenav, brandText } = props;
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // TEMA CONTEXT'İ
  const { isDark, setIsDark, brandTheme, setBrandTheme, navyTheme, setNavyTheme } = useThemeCustomizer();

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false); // Profil menüsü içindeki Tema akordeonu için
  const searchContainerRef = useRef(null);

  // --- KULLANICI BİLGİLERİ ---
  const getInitials = () => {
    if (!user) return "U";
    const first = user.name ? user.name.charAt(0) : "";
    const last = user.surname ? user.surname.charAt(0) : "";
    return `${first}${last}`.toUpperCase() || "U";
  };

  // --- ÇIKIŞ YAP ---
  const handleLogout = async (e) => {
    e.preventDefault();
    await logout();
    navigate("/auth/sign-in");
  };

  // --- ROL BAZLI ARAMA MOTORU ---
  const accessibleRoutes = useMemo(() => {
    const userRole = user?.role || "guest";
    let flatRoutes = [];

    routes.forEach((route) => {
      if (route.roles && !route.roles.includes(userRole)) return;

      if (route.layout && route.path && route.name) {
        flatRoutes.push({
          name: route.name,
          path: `${route.layout}/${route.path}`,
          icon: route.icon,
        });
      }

      if (route.items) {
        route.items.forEach((item) => {
          if (item.roles && !item.roles.includes(userRole)) return;
          if (item.path && item.name) {
            flatRoutes.push({
              name: item.name,
              path: `${route.layout}/${item.path}`,
              icon: route.icon,
            });
          }
        });
      }
    });
    return flatRoutes;
  }, [user]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return accessibleRoutes
        .filter((r) => r.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, 5);
  }, [searchQuery, accessibleRoutes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- TEMA ŞABLONLARI ---
  const themePresets = [
    { id: 'horizon', name: 'Orijinal', brand: 'default', navy: 'default', brandHex: '#422AFB', navyHex: '#111C44' },
    { id: 'luxury', name: 'Lüks Altın', brand: 'gold', navy: 'espresso', brandHex: '#d97706', navyHex: '#1c1917' },
    { id: 'forest', name: 'Zümrüt', brand: 'emerald', navy: 'deepocean', brandHex: '#10b981', navyHex: '#082730' },
    { id: 'soft', name: 'Lavanta', brand: 'lavender', navy: 'slate', brandHex: '#8b5cf6', navyHex: '#1e293b' },
    { id: 'soft-cyber', name: 'Soft Cyberpunk', brand: 'neon', navy: 'soft-dark', brandHex: '#06b6d4', navyHex: '#18181b' },
    {
      id: 'nordic-forest',
      name: 'Kuzey Ormanı',
      brand: 'forest',
      navy: 'nordic',
      brandHex: '#059669',
      navyHex: '#243b53'
    },
    {
      id: 'summer-peach',
      name: 'Yaz Esintisi',
      brand: 'peach',
      navy: 'slate',
      brandHex: '#ff7d29',
      navyHex: '#1e293b'
    },
    {
      id: 'nordic-sky',
      name: 'Nordic Sky',
      brand: 'lavender',      // Lavender (Soft Mor) ile kombin
      navy: 'nordic-pro',     // Yumuşak gece mavisi arkaplan
      brandHex: '#8b5cf6',
      navyHex: '#1e2d41'
    },
    {
      id: 'minimal-pro',
      name: 'Minimalist',
      brand: 'default',       // Orijinal Mavi ile kombin
      navy: 'space-grey',     // Apple stili antrasit arkaplan
      brandHex: '#422AFB',
      navyHex: '#232326'
    },
    {
      id: 'deep-slate',
      name: 'Derin Füme',
      brand: 'gold',          // Altın Vurgu rengi
      navy: 'midnight-slate', // Mavimsi füme arkaplan
      brandHex: '#d97706',
      navyHex: '#1e293b'
    }
  ];

  const applyPreset = (preset) => {
    setBrandTheme(preset.brand);
    setNavyTheme(preset.navy);
    setIsDark(true); // Şablonlar en iyi karanlık modda çalıştığı için karanlık moda zorluyoruz
  };

  return (
      <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-2xl bg-white/10 p-2 backdrop-blur-xl dark:bg-[#0b14374d]">

        {/* CSS: Gizli Scrollbar İçin */}
        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>

        {/* SOL TARAF */}
        <div className="ml-[6px]">
          <p className="shrink text-[33px] capitalize text-navy-700 dark:text-white">
            <Link to="#" className="font-bold capitalize hover:text-navy-700 dark:hover:text-white">
              {brandText}
            </Link>
          </p>
        </div>

        {/* SAĞ TARAF */}
        <div className="relative mt-[3px] flex h-[61px] w-[355px] flex-grow items-center justify-around gap-2 rounded-full bg-white px-2 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none md:w-[365px] md:flex-grow-0 md:gap-1 xl:w-[365px] xl:gap-2">

          {/* ARAMA ÇUBUĞU */}
          <div ref={searchContainerRef} className="relative flex h-full items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white xl:w-[225px]">
            <p className="pl-3 pr-2 text-xl"><FiSearch className="h-4 w-4 text-gray-400 dark:text-white" /></p>
            <input
                type="text"
                placeholder="Menüde Ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
            />

            {isSearchFocused && searchResults.length > 0 && (
                <div className="absolute top-14 left-0 w-full rounded-2xl bg-white p-2 shadow-2xl dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10 z-50">
                  {searchResults.map((result, idx) => (
                      <button
                          key={idx}
                          onClick={() => {
                            navigate(result.path);
                            setSearchQuery("");
                            setIsSearchFocused(false);
                          }}
                          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-all hover:bg-gray-50 dark:hover:bg-navy-700"
                      >
                        <span className="text-brand-500 dark:text-white">{result.icon}</span>
                        <span className="text-sm font-bold text-navy-700 dark:text-white">{result.name}</span>
                      </button>
                  ))}
                </div>
            )}
            {isSearchFocused && searchQuery && searchResults.length === 0 && (
                <div className="absolute top-14 left-0 w-full rounded-2xl bg-white p-4 shadow-2xl dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10 z-50 text-center text-sm font-medium text-gray-500">
                  Sonuç bulunamadı.
                </div>
            )}
          </div>

          <span
              className="flex cursor-pointer p-3 text-xl text-gray-600 dark:text-white xl:hidden hover:bg-gray-100 dark:hover:bg-navy-700 rounded-full transition-all"
              onClick={onOpenSidenav}
          >
            <FiAlignJustify className="h-5 w-5" />
          </span>

          {/* SADECE CONTEXT'E BAĞLI DARK MODE ŞALTERİ */}
          <div
              className="cursor-pointer p-3 text-gray-600 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-full transition-all dark:text-white"
              onClick={() => setIsDark(!isDark)}
          >
            {isDark ? (
                <RiSunFill className="h-5 w-5 text-amber-500" />
            ) : (
                <RiMoonFill className="h-5 w-5 text-brand-500" />
            )}
          </div>

          {/* PROFİL & TEMA DROPDOWN */}
          <Dropdown
              button={
                <div className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-brand-500 text-sm font-bold text-white shadow-lg shadow-brand-500/30 border-2 border-white dark:border-navy-800 transition-all hover:scale-105">
                  {getInitials()}
                </div>
              }
              children={
                <div className="flex w-72 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none border border-gray-100 dark:border-white/10 overflow-hidden">

                  {/* Başlık */}
                  <div className="p-4 pb-3">
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                      👋 Merhaba, {user?.name || "Kullanıcı"}
                    </p>
                  </div>
                  <div className="h-px w-full bg-gray-200 dark:bg-white/10" />

                  {/* TEMA SEÇİCİ AKORDEON */}
                  <div className="flex flex-col">
                    <button
                        onClick={(e) => {
                          e.stopPropagation(); // Menünün kapanmasını engeller
                          setIsThemeOpen(!isThemeOpen);
                        }}
                        className="w-full flex items-center justify-between p-4 pb-2 text-sm font-bold text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-400 transition-colors"
                    >
                      <div className="flex items-center gap-2"><MdOutlinePalette className="text-lg"/> Görünüm Şablonu</div>
                      <MdKeyboardArrowDown className={`text-lg transition-transform duration-300 ${isThemeOpen ? 'rotate-180' : ''}`}/>
                    </button>

                    {/* Genişleyen Şablon (Skeleton) Alanı */}
                    <div className={`transition-all duration-300 overflow-hidden ${isThemeOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="flex overflow-x-auto gap-3 px-4 pb-4 pt-2 hide-scrollbar">
                        {themePresets.map(preset => {
                          const isActive = brandTheme === preset.brand && navyTheme === preset.navy;
                          return (
                              <div
                                  key={preset.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    applyPreset(preset);
                                  }}
                                  className="flex flex-col items-center gap-1.5 flex-shrink-0 cursor-pointer group"
                              >
                                {/* Mini Dashboard İskeleti (Skeleton) */}
                                <div className={`w-[84px] h-14 rounded-lg overflow-hidden border-2 flex transition-transform group-hover:scale-105 ${isActive ? 'border-brand-500 shadow-md shadow-brand-500/30' : 'border-gray-200 dark:border-white/10'}`}>
                                  {/* Sol Menü / Brand Rengi */}
                                  <div className="w-[30%] h-full" style={{ backgroundColor: preset.brandHex }}></div>
                                  {/* Sağ İçerik / Navy Rengi */}
                                  <div className="w-[70%] h-full flex flex-col" style={{ backgroundColor: preset.navyHex }}>
                                    <div className="h-3 w-full border-b border-white/10 bg-white/5" />
                                    <div className="flex-1 p-1 flex items-end justify-end">
                                      {isActive && <MdCheck className="text-white text-xs drop-shadow-md" />}
                                    </div>
                                  </div>
                                </div>
                                <span className={`text-[10px] font-bold ${isActive ? 'text-brand-500 dark:text-brand-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                            {preset.name}
                                        </span>
                              </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="h-px w-full bg-gray-200 dark:bg-white/10" />

                  {/* PROFİL & ÇIKIŞ */}
                  <div className="flex flex-col p-4 gap-3">
                    <button
                        onClick={() => navigate(`/${user?.role || 'admin'}/profile`)}
                        className="text-left text-sm font-bold text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-400 transition-colors"
                    >
                      Profil Ayarları
                    </button>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 text-sm font-bold text-red-500 hover:text-red-600 transition-colors"
                    >
                      <MdLogout className="text-lg" /> Çıkış Yap
                    </button>
                  </div>

                </div>
              }
              classNames={"py-2 top-8 -left-[240px] w-max"}
          />
        </div>
      </nav>
  );
};

export default Navbar;